import React from 'react';
import { isEmpty } from 'lodash';

import { LoadingSwitch, SortableTable } from '~/components/core';
import Chip from '~/components/core/Atomic/Chip/Chip';
import CollapsibleWrapper from '~/components/core/Collapsible/CollapsibleWrapper';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import { PencilIcon } from '~/components/icons';
import InlineIconButton from '~/components/InlineIconButton';
import useOrganization from '~/components/OrganizationContext';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import { serverDateToLocal } from '~/DateTimeUtils';
import { getLobDescription } from '~/Utils/lobUtils';

import { stripDraftIdPrefix } from './draftName';
import type { IncidentConfigurationDraftSummary, IncidentConfigurationDraftSummaryList } from './types';

interface IncidentConfigurationDraftsListProps {
  displayName: string;
  onEditDraft: (draft: IncidentConfigurationDraftSummary) => void;
}

// Generic drafts table shown under the live configurations table on the list page - it lists
// every incident-configuration draft for the organization (across all configurations), mirroring
// the "Saved Drafts" collapsible on the /fnol page. Each row opens the editor loaded with that
// draft so an internal admin can save it as a draft again or promote it to the live config.
const IncidentConfigurationDraftsList: React.FC<IncidentConfigurationDraftsListProps> = ({
  displayName,
  onEditDraft,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { subOrganizationEnabled, subOrganizations } = useOrganization();
  const { lobConfigurationsDict } = useLobConfiguration();

  const route = `/sysconfig/api/v1/organizations/${organization.id}/incident_configuration_drafts`;
  const { isLoading, isError, data: drafts = [] } = useDataFetcher<IncidentConfigurationDraftSummaryList>(route, {});

  // Mirror the /fnol drafts collapsible: hide it entirely when there is nothing to show.
  if (!isLoading && !isError && isEmpty(drafts)) {
    return null;
  }

  const columns = [
    {
      id: 'name',
      label: 'Draft Name',
      specialCell: ({ id, name }: IncidentConfigurationDraftSummary) =>
        stripDraftIdPrefix(id, name) ? name : `#${id} Untitled draft`,
      // Every name opens with "#<id> ", so sorting the stored value orders by id as text
      // ("#10" before "#2"). Sort on the operator's part of the name instead.
      specialCmpFunc: (a: IncidentConfigurationDraftSummary, b: IncidentConfigurationDraftSummary) =>
        stripDraftIdPrefix(a.id, a.name).localeCompare(stripDraftIdPrefix(b.id, b.name)),
    },
    {
      id: 'incident_configuration_display_name',
      label: displayName,
      disableSort: true,
    },
    {
      id: 'sub_organization_id',
      label: 'Sub Organization',
      isHidden: !subOrganizationEnabled,
      disableSort: true,
      specialCell: ({ sub_organization_id }: IncidentConfigurationDraftSummary) =>
        subOrganizations?.find((subOrg: { id: number }) => subOrg.id === sub_organization_id)?.name,
    },
    {
      id: 'lob',
      label: 'Line of Business',
      disableSort: true,
      specialCell: ({ lob }: IncidentConfigurationDraftSummary) => (
        <div>
          <Chip label={getLobDescription(lob, lobConfigurationsDict)} size="small" />
        </div>
      ),
    },
    {
      // Surfaced so an internal admin can cross-reference the draft in the investigation tool, which
      // reads incident_configuration_draft rows from the data warehouse rather than from CMS.
      id: 'id',
      label: 'Draft ID',
      width: 80,
    },
    {
      id: 'base_version',
      label: 'Base Version',
      width: 80,
    },
    {
      id: 'last_updated_datetime',
      label: 'Last Updated',
      specialCell: ({ last_updated_datetime }: IncidentConfigurationDraftSummary) =>
        serverDateToLocal(last_updated_datetime),
    },
    {
      id: 'last_updated_by_username',
      label: 'Updated By',
    },
    {
      id: 'actions',
      label: 'Actions',
      disableSort: true,
      width: '70px',
      specialCell: (draft: IncidentConfigurationDraftSummary) => (
        <InlineIconButton icon={PencilIcon} onClick={() => onEditDraft(draft)} tooltipTitle="Edit draft" />
      ),
    },
  ];

  const visibleColumns = subOrganizationEnabled ? columns : columns.filter((col) => col.id !== 'sub_organization_id');

  return (
    <CollapsibleWrapper
      title="Saved Drafts"
      actionCard
      noBorder
      noHorizontalPadding
      backgroundColor={CollapsibleWrapper.BACKGROUND_COLOR.WHITE}
    >
      <LoadingSwitch isLoading={isLoading} isError={isError}>
        <SortableTable columns={visibleColumns} rows={drafts} stickyHeader maxHeight="50vh" />
      </LoadingSwitch>
    </CollapsibleWrapper>
  );
};

export default IncidentConfigurationDraftsList;
